import { api } from "./client";

// O2C Protocol — Order-to-Cash types

export type O2COrderStatus =
  | "pending"
  | "quoted"
  | "negotiating"
  | "accepted"
  | "delivered"
  | "disputed"
  | "completed"
  | "cancelled"
  | "refunded";

export type O2CMessageType =
  | "DISCOVER"
  | "INTENT"
  | "QUOTE"
  | "COUNTER"
  | "COMMIT"
  | "FULFIL"
  | "DISPUTE"
  | "SETTLE";

export interface O2COrder {
  id: string;
  buyerCompanyId: string;
  sellerCompanyId: string;
  productId?: string;
  productName: string;
  quantity: number;
  unitPriceUSD: number;
  totalUSD: number;
  feeUSD: number;
  netUSD: number;
  currency: string;
  status: O2COrderStatus;
  orderType: string;
  deliveryWorkerId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateOrderPayload {
  buyerCompanyId: string;
  sellerCompanyId: string;
  productId?: string;
  productName: string;
  quantity: number;
  unitPriceUSD: number;
  currency?: string;
  orderType?: string;
}

export interface O2CMessage {
  id: string;
  sessionId: string;
  type: O2CMessageType;
  senderCompanyId: string;
  receiverCompanyId: string;
  orderId?: string;
  payload: string;
  signature?: string;
  algorandTxId?: string;
  createdAt: string;
  deliveredAt?: string;
}

export interface O2CSession {
  id: string;
  initiatorId: string;
  counterpartyId: string;
  topic: string;
  orderId?: string;
  status: string;
  offerCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface DisputeResponse {
  disputeId: string;
  orderId: string;
  status: "disputed";
  reason: string;
  filedByCompanyId: string;
  createdAt: string;
}

export interface ResolveDisputeResponse {
  orderId: string;
  status: "refunded" | "completed";
  resolution: string;
  refundBuyer: boolean;
  resolvedAt: string;
}

export interface SendMessagePayload {
  sessionId?: string;
  type: O2CMessageType;
  senderCompanyId: string;
  receiverCompanyId: string;
  orderId?: string;
  payload: Record<string, unknown>;
  signature?: string;
  algorandTxId?: string;
}

export interface CreateSessionPayload {
  initiatorId: string;
  counterpartyId: string;
  topic: string;
  orderId?: string;
}

export const o2cApi = {
  // Orders
  createOrder: (data: CreateOrderPayload) =>
    api.post<O2COrder>("/commerce/orders", data),

  listOrders: (companyId: string) =>
    api.get<O2COrder[]>(`/companies/${encodeURIComponent(companyId)}/orders`),

  updateOrderStatus: (orderId: string, status: O2COrderStatus) =>
    api.post<{ id: string; status: string }>(
      `/commerce/orders/${orderId}/status`,
      { status },
    ),

  settleOrder: (orderId: string) =>
    api.post<{ orderId: string; totalUSD: number; feeUSD: number; netUSD: number; status: string }>(
      `/commerce/orders/${orderId}/settle`,
      {},
    ),

  // Dispute
  disputeOrder: (orderId: string, reason: string, filedByCompanyId: string) =>
    api.post<DisputeResponse>(
      `/commerce/orders/${orderId}/dispute`,
      { reason, filedByCompanyId },
    ),

  resolveDispute: (orderId: string, resolution: string, refundBuyer: boolean) =>
    api.post<ResolveDisputeResponse>(
      `/commerce/orders/${orderId}/dispute/resolve`,
      { resolution, refundBuyer },
    ),

  // Agent messaging (O2C bus)
  createSession: (data: CreateSessionPayload) =>
    api.post<O2CSession>("/agents/bus/session", data),

  getSession: (sessionId: string) =>
    api.get<{ sessionId: string; messages: O2CMessage[]; count: number }>(
      `/agents/bus/session/${sessionId}`,
    ),

  sendMessage: (data: SendMessagePayload) =>
    api.post<{ id: string; sessionId: string; type: string; ok: boolean; createdAt: string }>(
      "/agent/messages",
      data,
    ),

  pollMessages: (companyId: string, since?: string) =>
    api.get<{ messages: O2CMessage[]; count: number }>(
      `/agent/messages?companyId=${encodeURIComponent(companyId)}${since ? `&since=${encodeURIComponent(since)}` : ""}`,
    ),
};
